import React, { useEffect, useState, useRef } from "react";
import { useLocation, useParams } from "react-router";
import classNames from "classnames";
import { secondsToTimeString } from "helpers/utility/formatters";
import ImagePreview from "views/pages/EnrichmentPage/ImagePreview";
import ActionButtons from "./ActionButtons";
import ErrorIcon from "./Icons/ErrorIcon";
import { VLLoader } from "views/uikit/VLLoader";
import VideoBox from "./VideoBox";
import styles from "./style.module.scss";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { VQL_UPGRADE } from "helpers/constants/filters";
import IconButton from "../../../uikit/Buttons/IconButton";
import { VLCropperRef } from "../../../uikit/VLCropper";
import CroppablePreview from "./CroppablePreview";
import { useMediaCropping } from "../../../../hooks/useMediaCropping";
import { CropIcon } from "../../../pages/DataPage/CardsView/DataPageFilters/SearchContainer/Icons";
import { AUTOMATIC_FLAT_RESPONSE_PARAMETER } from "../../../../helpers/constants/miscellaneous";
import { useSearchParams } from "react-router-dom";

const ErrorPreview = () => {
  return (
    <div className={styles.errorPreview}>
      <ErrorIcon />
      preview is not available
    </div>
  );
};

interface BodyProps {
  imageSrc: string;
  timestamp: number;
  mdLabels: string[];
  previewCount: number;
  hoveredAssetData: any;
  videoSrc: string | null;
  showSimilarityButton: boolean;
  boundingBoxes: number[][] | null;
  type: "IMAGE" | "OBJECT" | "VIDEO";
  onSimilarityClick: (cropData?: any, negation?: boolean) => void;
  onDetailsClick: (e: any) => void;
  isFindSimilarButtonSelected: boolean;
  setIsFindSimilarButtonSelected: (value: boolean) => void;
}

const Body = ({
  type,
  mdLabels,
  imageSrc,
  videoSrc,
  timestamp,
  previewCount,
  boundingBoxes,
  hoveredAssetData,
  showSimilarityButton,
  onDetailsClick,
  onSimilarityClick,
  isFindSimilarButtonSelected,
  setIsFindSimilarButtonSelected,
}: BodyProps) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [currTime, setCurrTime] = useState(0);
  const { getSvgCropperSrc, handleCroppedMediaUploadAndNavigate } =
    useMediaCropping();

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const { enrichDatasetId, datasetId } = useParams();

  const { error, id: hoveredId } = hoveredAssetData;

  const showVideo = !error && videoLoaded;
  const showTimestamp = !!timestamp && showVideo;
  const showActionButtons =
    !(error || videoError) && hoveredId && !enrichDatasetId;
  const VIDEO_DURATION = 4;
  const showLoadingOverLay = !!videoSrc && !videoLoaded && !videoError;
  const timestampText = `Timestamp ${secondsToTimeString(currTime)}`;

  const cardBodyClassnames = classNames(styles.cardBody, {
    [styles.defaultCursor]: !!enrichDatasetId,
  });

  const timestampClassNames = classNames(styles.assetPreviewTimestamp, {
    [styles.higherMarginBottom]: previewCount > 2,
  });

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  const handleVideoError = () => {
    setVideoError(true);
  };

  const handleTimeUpdate = (newTime: number) => {
    if (newTime) {
      let temp = Math.floor(newTime);
      if (temp !== currTime) {
        setCurrTime(temp);
      }
    } else setCurrTime(0);
  };

  const onUpdateAssetData = () => {};

  //handle the video reloads when selection is made from carousel
  useEffect(() => {
    setVideoError(false);
    setVideoLoaded(false);
    setIsFindSimilarButtonSelected(false); // Reset cropper state when asset changes
  }, [hoveredId, videoSrc, setIsFindSimilarButtonSelected]);

  const isVQLUpgradeEnabled = useFeatureFlagService(VQL_UPGRADE).enabled;
  const cropperRef = useRef<VLCropperRef>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleExitCrop = () => {
    setIsFindSimilarButtonSelected(false);
  };

  const handleSelectionSubmit = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (cropperRef.current) {
      const cropResult = cropperRef.current.getCropData();
      if (cropResult?.croppedCanvas && cropResult?.cropData) {
        try {
          if (type === "VIDEO") {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set(AUTOMATIC_FLAT_RESPONSE_PARAMETER, "true");

            await handleCroppedMediaUploadAndNavigate({
              mediaType: "VIDEO",
              videoElement: videoRef.current!,
              imageData: null,
              cropCoordinates: cropResult.cropData,
              datasetId: datasetId!,
              dataPath: pathname + `?${newSearchParams.toString()}`,
              negation: undefined,
            });
          } else {
            // Pass the crop data to the similarity search
            onSimilarityClick(cropResult.cropData);
          }
          setIsFindSimilarButtonSelected(false);
        } catch (error) {
          console.error("Failed to process cropped media:", error);
          return;
        }
      }
    }
  };

  // Handle Find Similar button click
  const handleFindSimilarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFindSimilarButtonSelected(true);
  };

  const renderDeprecatedPreview = () => {
    switch (type) {
      case "OBJECT":
      case "IMAGE":
        return (
          <ImagePreview
            hoverDelay={0}
            src={imageSrc}
            labels={mdLabels}
            showAssetPreview={false}
            onUpdateAssetData={onUpdateAssetData}
            boundingBoxes={boundingBoxes || []}
          />
        );

      case "VIDEO":
        return (
          <>
            {showLoadingOverLay && (
              <div className={styles.loadingOverlay}>
                <VLLoader themeType="light" size="medium" />
              </div>
            )}

            <VideoBox
              src={videoSrc}
              offset={timestamp || 0}
              duration={VIDEO_DURATION}
              showVideoBox={videoLoaded}
              handleVideoLoad={handleVideoLoad}
              handleVideoError={handleVideoError}
              handleTimeUpdate={handleTimeUpdate}
              videoRef={videoRef}
              externalPaused={isFindSimilarButtonSelected}
            />
          </>
        );
    }
  };

  const renderPreview = () => {
    if (videoError) {
      return <ErrorPreview />;
    }

    // If VQL upgrade is disabled, use simple preview (backward compatibility)
    if (!isVQLUpgradeEnabled) {
      return renderDeprecatedPreview();
    }

    // VQL upgrade enabled - use croppable preview
    switch (type) {
      case "OBJECT":
      case "IMAGE":
        return (
          <CroppablePreview
            isVQLUpgradeEnabled={isVQLUpgradeEnabled}
            isFindSimilarButtonSelected={isFindSimilarButtonSelected}
            cropperRef={cropperRef}
            cropperSrc={getSvgCropperSrc(
              type,
              videoRef.current || undefined,
              undefined,
              imageSrc,
            )}
            onSubmit={handleSelectionSubmit}
          >
            <ImagePreview
              hoverDelay={0}
              src={imageSrc}
              labels={mdLabels}
              showAssetPreview={false}
              onUpdateAssetData={onUpdateAssetData}
              boundingBoxes={boundingBoxes || []}
            />
          </CroppablePreview>
        );

      case "VIDEO":
        return (
          <CroppablePreview
            isVQLUpgradeEnabled={isVQLUpgradeEnabled}
            isFindSimilarButtonSelected={isFindSimilarButtonSelected}
            cropperRef={cropperRef}
            cropperSrc={getSvgCropperSrc(
              type,
              videoRef.current || undefined,
              undefined,
              imageSrc,
            )}
            onSubmit={handleSelectionSubmit}
          >
            {showLoadingOverLay && (
              <div className={styles.loadingOverlay}>
                <VLLoader themeType="light" size="medium" />
              </div>
            )}

            <VideoBox
              src={videoSrc}
              offset={timestamp || 0}
              duration={VIDEO_DURATION}
              showVideoBox={videoLoaded}
              handleVideoLoad={handleVideoLoad}
              handleVideoError={handleVideoError}
              handleTimeUpdate={handleTimeUpdate}
              videoRef={videoRef}
              externalPaused={isFindSimilarButtonSelected}
            />
          </CroppablePreview>
        );
    }
  };

  return (
    <div
      onClick={onDetailsClick}
      className={cardBodyClassnames}
      style={{ backgroundImage: `url(${imageSrc})` }}
    >
      {renderPreview()}

      {showActionButtons && !isVQLUpgradeEnabled && (
        <ActionButtons
          showSimilarityButton={showSimilarityButton}
          onSimilarityClick={(negation) =>
            onSimilarityClick(undefined, negation)
          }
        />
      )}

      {showActionButtons && isVQLUpgradeEnabled && (
        <div>
          {!isFindSimilarButtonSelected && (
            <ActionButtons
              showSimilarityButton={showSimilarityButton}
              onSimilarityClick={(negation) =>
                onSimilarityClick(undefined, negation)
              }
            />
          )}

          <div
            className={classNames(styles.findSimilarButton, {
              [styles.cropperOpen]: isFindSimilarButtonSelected,
            })}
          >
            <IconButton
              onClick={handleFindSimilarClick}
              title={
                isFindSimilarButtonSelected
                  ? `Exit Crop`
                  : `Crop to find similar`
              }
              text=""
              variant={!isFindSimilarButtonSelected ? "success" : undefined}
              icon={
                <div className={styles.cropIcon}>
                  <CropIcon />
                </div>
              }
              className={classNames(styles.iconButton, {
                [styles.iconButtonTransparent]: isFindSimilarButtonSelected,
              })}
              onClose={isFindSimilarButtonSelected ? handleExitCrop : undefined}
              active={isFindSimilarButtonSelected}
            />
          </div>
        </div>
      )}

      {showTimestamp && (
        <div className={timestampClassNames}>{timestampText}</div>
      )}
    </div>
  );
};

export default Body;
